import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import { Button, Divider, Grid, IconButton, LinearProgress } from '@mui/material';
import type { GridRowId, GridRowModesModel, GridRowsProp } from '@mui/x-data-grid';
import { GridRowModes } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { descargarCaratula } from '../../api-client/compras/compras.api';
import type { HistoricoArete } from '../../api-client/pre-compras/aretes.dto';
import type { Compra, CompraBusqueda } from '../../stores/compras/compra';
import type { Corral } from '../../stores/corrales/corral';
import type { PersonaEntregaResumen } from '../../stores/personal-entrega/persona-entrega';
import type { PersonaRecibeResumen } from '../../stores/personal-recibe/persona-recibe';
import type { PersonaVendeResumen } from '../../stores/personal-vende/persona-vende';
import { useUIStore } from '../../stores/ui/ui.store';
import generateRandonId from '../../utils/generate-random-id';
import transformToOptions from '../../utils/transform-to-options';
import PersonalEntregaSearchDialog from '../personal-entrega/PersonalEntregaSearchDialog';
import PersonalRecibeSearchDialog from '../personal-recibe/PersonalRecibeSearchDialog';
import PersonalVendeSearchDialog from '../personal-vende/PersonalVendeSearchDialog';
import AppDialog from '../ui/AppDialog';
import AppForm from '../ui/AppForm';
import AppTextField from '../ui/AppTextField';
import ComboBox from '../ui/Combobox';
import HistorialAreteDialog from '../ui/HistorialAreteDialog';
import type { AnimalCompraRow } from './AnimalCompraRow';
import AnimalesCompra from './AnimalesCompra';
import { validationSchema, type CompraFormValues } from './compra.schema';
import CompraSearchDialog from './CompraSearchDialog';

interface CompraFormProps {
  compra: Compra;
  corrales: Array<Corral>;
  historialSiiniga: Array<HistoricoArete>;
  historialTemporal: Array<HistoricoArete>;
  cargarHistorialSiiniga: (folio: string, partida: number) => Promise<void>;
  cargarHistorialTemporal: (folio: string, partida: number) => Promise<void>;
  onCancel: () => Promise<void>;
  onClear: () => Promise<void>;
  onSave: (values: CompraFormValues) => Promise<boolean>;
  onCompraSelected: (compra: CompraBusqueda) => void;
}

function convertToAnimalesCompraRow(compra: Compra): Array<AnimalCompraRow> {
  return !compra?.partidas?.length
    ? []
    : compra.partidas.map((p) => ({
        id: generateRandonId(),
        folio: compra.folioInterno,
        partida: p.partida,
        areteSiiniga: p.areteSiiniga,
        areteTemporal: p.areteTemporal,
        tienePapeles: p.tienePapeles,
        reemo: p.reemo,
        idTipoGanado: p.idTipoGanado,
        tipoGanado: p.tipoGanado,
        kgCompra: p.kgCompra,
        kgLlegada: p.kgLlegada,
        descuentoPeso: p.descuentoPeso,
        kgNetos: p.kgNetos,
        precio: p.precio,
        importe: p.importe,
        omiteDescuento: false,
        isNew: false
      }));
}

function convertToRowsModel(animales: Array<AnimalCompraRow>) {
  return animales.reduce((acc, animal) => {
    acc[animal.id] = { mode: GridRowModes.View };
    return acc;
  }, {} as GridRowModesModel);
}

const CompraForm = ({
  compra,
  corrales,
  historialSiiniga,
  historialTemporal,
  cargarHistorialSiiniga,
  cargarHistorialTemporal,
  onCancel,
  onClear,
  onSave,
  onCompraSelected
}: CompraFormProps) => {
  const compraCargada = compra.folioInterno !== undefined;
  const activo = compra?.activa === true;
  const esConversion = compra?.esCompra === false;
  const inactivo = !activo;
  const [editarGenerales, setEditarGenerales] = useState(false);
  const [rows, setRows] = useState<GridRowsProp<AnimalCompraRow>>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [showPersonalEntregaDialog, setShowPersonalEntregaDialog] = useState(false);
  const [showPersonalRecibeDialog, setShowPersonalRecibeDialog] = useState(false);
  const [showPersonalVendeDialog, setShowPersonalVendeDialog] = useState(false);
  const [showComprasDialog, setShowComprasDialog] = useState(false);
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [showHistorialSiiniga, setShowHistorialSiiniga] = useState(false);
  const [showHistorialTemporal, setShowHistorialTemporal] = useState(false);
  const { loading, showWarningToast } = useUIStore();

  const initialValues: CompraFormValues = {
    folioInterno: compra?.folioInterno ?? '',
    fecha: compra.fecha ? dayjs(compra.fecha) : dayjs(new Date()),
    referencia: compra?.referencia ?? '',
    idPersonaVende: compra?.idPersonaVende ?? 0,
    vendidoPor: compra?.vendidoPor ?? '',
    idPersonaRecibe: compra?.idPersonaRecibe ?? 0,
    recibidoPor: compra?.recibidoPor ?? '',
    idPersonaEntrega: compra?.idPersonaEntrega ?? 0,
    entregadoPor: compra?.entregadoPor ?? '',
    idCorral: compra?.idCorral ?? 0,
    totalAnimales: compra?.totalAnimales ?? 0,
    animales: convertToAnimalesCompraRow(compra)
  };
  const form = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const result = await onSave(values);
      if (result) {
        reset();
      }
    }
  });

  const corralesOptions = transformToOptions(corrales);

  const handleEditRow = useCallback((id: GridRowId) => {
    setRowModesModel((prevRowModesModel) => ({
      ...prevRowModesModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'precio' }
    }));
  }, []);
  const handleSaveRow = useCallback((id: GridRowId) => {
    setRowModesModel((prevRowModesModel) => ({
      ...prevRowModesModel,
      [id]: { mode: GridRowModes.View }
    }));
  }, []);
  const handleCancel = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prevRowModesModel) => ({
        ...prevRowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true }
      }));

      const row = rows.find((r) => r.id === id);
      if (row?.isNew) {
        setRows((prev) => prev.filter((r) => r.id !== id));
        form.setFieldValue(
          'animales',
          form.values.animales.filter((r) => r.id !== id)
        );
      }
    },
    [form]
  );
  const handleRowUpdate = useCallback(
    async (newRow: AnimalCompraRow) => {
      const updatedRow = { ...newRow, isNew: false };
      setRows((prev) => prev.map((row) => (row.id === newRow.id ? updatedRow : row)));
      await form.setFieldValue(
        'animales',
        form.values.animales.map((row) => (row.id === newRow.id ? updatedRow : row))
      );
      return updatedRow;
    },
    [form.values]
  );

  const handlePersonaEntregaSelect = async (registro: PersonaEntregaResumen) => {
    await form.setFieldValue('idPersonaEntrega', registro.id, true);
    await form.setFieldValue('entregadoPor', registro.nombre, true);
    setShowPersonalEntregaDialog(false);
  };
  const handlePersonaRecibeSelect = async (registro: PersonaRecibeResumen) => {
    await form.setFieldValue('idPersonaRecibe', registro.id, true);
    await form.setFieldValue('recibidoPor', registro.nombre);
    setShowPersonalRecibeDialog(false);
  };
  const handlePersonaVendeSelect = async (registro: PersonaVendeResumen) => {
    await form.setFieldValue('idPersonaVende', registro.id, true);
    await form.setFieldValue('vendidoPor', registro.nombre);
    setShowPersonalVendeDialog(false);
  };
  const handleComprasSelect = async (compra: CompraBusqueda) => {
    setShowComprasDialog(false);
    onCompraSelected(compra);
  };

  const handleConfirmSave = () => {
    setShowConfirmSave(false);

    form.handleSubmit();
    if (!form.isValid) {
      if (typeof form.errors.animales === 'string') {
        showWarningToast(form.errors.animales.toString());
      } else {
        showWarningToast(
          'Aún faltan datos obligatorios en su captura, de favor verifíquelo para poder continuar'
        );
      }
    }
  };

  const handleConfirmCancel = async () => {
    setShowConfirmCancel(false);
    await onCancel();
  };

  const handleClickHistorialSiiniga = (folio: string, partida: number) => {
    cargarHistorialSiiniga(folio, partida).then(() => setShowHistorialSiiniga(true));
  };
  const handleClickHistorialTemporal = (folio: string, partida: number) => {
    cargarHistorialTemporal(folio, partida).then(() => setShowHistorialTemporal(true));
  };

  const handleDescargarCompra = useCallback(() => {
    descargarCaratula(compra.folioInterno)
      .then((response) => {
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Compra_${compra.folioInterno}.pdf`);
        document.body.appendChild(link);
        link.click();
      })
      .catch(() => {
        showWarningToast('Error al descargar la carátula de la compra');
      });
  }, [compra?.folioInterno]);

  useEffect(() => {
    const animales = convertToAnimalesCompraRow(compra);
    setRows(animales);
    setRowModesModel(convertToRowsModel(animales));
    form.setValues({
      ...initialValues,
      animales
    });
  }, [compra.folioInterno, compra.partidas]);

  const reset = async () => {
    form.setValues(initialValues);
    const rows = initialValues.animales.map((a, i) => ({
      ...a,
      isNew: false,
      folio: initialValues.folioInterno,
      partida: i + 1,
      omiteDescuento: false
    }));
    setRows(rows);
    setRowModesModel(convertToRowsModel(rows));
  };

  return (
    <>
      <AppForm title="Compra de Ganado" lineSpacing={1}>
        <Grid size={12}>
          <Grid container spacing={2}>
            <Grid size={3}>
              <AppTextField
                name="folioInterno"
                label="Folio Interno"
                value={form.values.folioInterno}
                disabled={true}
                endAdornment={
                  <IconButton onClick={() => setShowComprasDialog(true)}>
                    <SearchIcon />
                  </IconButton>
                }
              />
            </Grid>
            <Grid size={2}>
              <DatePicker
                label="Fecha"
                value={form.values.fecha}
                disabled={loading || inactivo || !editarGenerales}
                onChange={form.handleChange}
              />
            </Grid>
            <Grid size={3}>
              <AppTextField
                label="Referencia"
                name="referencia"
                disabled={loading || inactivo || !editarGenerales}
                value={form.values.referencia}
                maxLength={25}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container spacing={2}>
            <Grid size={5}>
              <AppTextField
                label="Vendido Por"
                value={form.values.vendidoPor}
                readOnly={true}
                endAdornment={
                  inactivo || loading || !editarGenerales ? (
                    <></>
                  ) : (
                    <>
                      <IconButton onClick={() => setShowPersonalVendeDialog(true)}>
                        <SearchIcon />
                      </IconButton>
                      <IconButton>
                        <AddIcon />
                      </IconButton>
                    </>
                  )
                }
                error={form.touched.idPersonaVende && Boolean(form.errors.idPersonaVende)}
                helperText={form.touched.idPersonaVende ? form.errors.idPersonaVende : ''}
              />
            </Grid>
            <Grid size={3}>
              <ComboBox
                label="Corral de reciba"
                name="idCorral"
                disabled={loading || inactivo || !editarGenerales}
                value={form.values.idCorral}
                options={corralesOptions}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                error={form.touched.idCorral && Boolean(form.errors.idCorral)}
                helperText={form.touched.idCorral ? form.errors.idCorral : ''}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container spacing={2}>
            <Grid size={5}>
              <AppTextField
                label="Recibido Por"
                value={form.values.recibidoPor}
                readOnly={true}
                endAdornment={
                  inactivo || loading || !editarGenerales ? (
                    <></>
                  ) : (
                    <>
                      <IconButton onClick={() => setShowPersonalRecibeDialog(true)}>
                        <SearchIcon />
                      </IconButton>
                      <IconButton>
                        <AddIcon />
                      </IconButton>
                    </>
                  )
                }
                error={form.touched.idPersonaRecibe && Boolean(form.errors.idPersonaRecibe)}
                helperText={form.touched.idPersonaRecibe ? form.errors.idPersonaRecibe : ''}
              />
            </Grid>
            <Grid size={3}>
              <AppTextField
                name="totalAnimales"
                label="Número de animales"
                value={form.values.totalAnimales}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                disabled={true}
                error={form.touched.totalAnimales && Boolean(form.errors.totalAnimales)}
                helperText={form.touched.totalAnimales ? form.errors.totalAnimales : ''}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container spacing={2}>
            <Grid size={5}>
              <AppTextField
                label="Entregado Por"
                value={form.values.entregadoPor}
                readOnly={true}
                endAdornment={
                  inactivo || loading || !editarGenerales ? (
                    <></>
                  ) : (
                    <>
                      <IconButton onClick={() => setShowPersonalEntregaDialog(true)}>
                        <SearchIcon />
                      </IconButton>
                      <IconButton>
                        <AddIcon />
                      </IconButton>
                    </>
                  )
                }
                error={form.touched.idPersonaEntrega && Boolean(form.errors.idPersonaEntrega)}
                helperText={form.touched.idPersonaEntrega ? form.errors.idPersonaEntrega : ''}
              />
            </Grid>
            <Grid size={3}>
              <AppTextField
                label="Tipo de documento"
                value={compra?.tipo?.toLocaleUpperCase() || 'COMPRA'}
                disabled={true}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>{loading ? <LinearProgress color="secondary" /> : <></>}</Grid>
        <Grid>
          <AnimalesCompra
            readonly={inactivo || compra?.esCompra === true}
            rows={rows}
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            onClickHistorialSiiniga={handleClickHistorialSiiniga}
            onClickHistorialTemporal={handleClickHistorialTemporal}
            onCancel={handleCancel}
            onEdit={handleEditRow}
            onSave={handleSaveRow}
            onRowUpdate={handleRowUpdate}
          />
        </Grid>
        <Divider />
        <Grid>
          <Grid container spacing={2} justifyContent="flex-end">
            {compraCargada && esConversion ? (
              <Grid>
                <Button
                  type="button"
                  variant="contained"
                  color="secondary"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setShowConfirmSave(true)}
                  disabled={loading || inactivo}
                  disableElevation
                >
                  Convertir en Compra
                </Button>
              </Grid>
            ) : (
              <></>
            )}
            {compraCargada && !esConversion ? (
              <>
                <Grid>
                  <Button
                    type="button"
                    variant="contained"
                    color="secondary"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => setShowConfirmSave(true)}
                    disabled={loading || inactivo}
                    disableElevation
                  >
                    Guardar
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    disabled={loading || inactivo}
                    disableElevation
                    onClick={() => setEditarGenerales((prev) => !prev)}
                  >
                    Modificar Generales
                  </Button>
                </Grid>
              </>
            ) : (
              <></>
            )}
            {compraCargada && !esConversion ? (
              <Grid>
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  disabled={loading}
                  disableElevation
                  onClick={handleDescargarCompra}
                >
                  Imprimir
                </Button>
              </Grid>
            ) : (
              <></>
            )}
            {compraCargada ? (
              <Grid>
                <Button
                  variant="contained"
                  startIcon={<CancelIcon />}
                  disabled={loading || inactivo}
                  disableElevation
                  onClick={reset}
                >
                  Deshacer cambios
                </Button>
              </Grid>
            ) : (
              <></>
            )}
            {compraCargada && activo && !esConversion ? (
              <Grid>
                <Button
                  variant="contained"
                  onClick={() => setShowConfirmCancel(true)}
                  startIcon={<DeleteIcon />}
                  disabled={loading || inactivo}
                  disableElevation
                >
                  Cancelar
                </Button>
              </Grid>
            ) : (
              <></>
            )}
            <Grid>
              <Button
                variant="contained"
                startIcon={<CleaningServicesIcon />}
                disabled={loading || inactivo}
                disableElevation
                onClick={onClear}
              >
                Limpiar
              </Button>
            </Grid>
            <Grid>
              <Button
                variant="contained"
                startIcon={<ExitToAppIcon />}
                component={RouterLink}
                to="/"
                disableElevation
              >
                Salir
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </AppForm>
      <PersonalEntregaSearchDialog
        open={showPersonalEntregaDialog}
        onClose={() => setShowPersonalEntregaDialog(false)}
        onSelect={handlePersonaEntregaSelect}
      />
      <PersonalRecibeSearchDialog
        open={showPersonalRecibeDialog}
        onClose={() => setShowPersonalRecibeDialog(false)}
        onSelect={handlePersonaRecibeSelect}
      />
      <PersonalVendeSearchDialog
        open={showPersonalVendeDialog}
        onClose={() => setShowPersonalVendeDialog(false)}
        onSelect={handlePersonaVendeSelect}
      />
      <CompraSearchDialog
        open={showComprasDialog}
        onClose={() => setShowComprasDialog(false)}
        onSelect={handleComprasSelect}
      />
      <AppDialog
        open={showConfirmSave}
        onClose={() => setShowConfirmSave(false)}
        title="Confirme"
        message={
          esConversion
            ? '¿Está seguro de convertir la Pre-Compra en Compra?'
            : '¿Está seguro de actualizar la Compra?'
        }
        buttons={
          <>
            <Button onClick={handleConfirmSave}>Sí, guardar</Button>
            <Button onClick={() => setShowConfirmSave(false)}>No</Button>
          </>
        }
      />
      <AppDialog
        open={showConfirmCancel}
        onClose={() => setShowConfirmCancel(false)}
        title="Confirme"
        message="¿Está seguro de cancelar la Compra?"
        buttons={
          <>
            <Button onClick={handleConfirmCancel}>Sí, cancelar</Button>
            <Button onClick={() => setShowConfirmCancel(false)}>No</Button>
          </>
        }
      />
      <HistorialAreteDialog
        titulo="SIINIGA"
        historial={historialSiiniga}
        open={showHistorialSiiniga}
        onClose={() => setShowHistorialSiiniga(false)}
        formatter={(arete) => arete.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3')}
      />
      <HistorialAreteDialog
        titulo="TEMPORAL"
        historial={historialTemporal}
        open={showHistorialTemporal}
        onClose={() => setShowHistorialTemporal(false)}
        formatter={(arete) => arete.replace(/([a-zA-Z]{1})([0-9]{1,3})/, '$1-$2')}
      />
    </>
  );
};

export default CompraForm;
